import cx from "classnames";

import CS from "metabase/css/core/index.css";
import { PLUGIN_LOGO_ICON_COMPONENTS } from "metabase/plugins";
import { useMantineTheme } from "metabase/ui";
import { SEMIPUBLIC_COLORS } from "metabase/ui/colors/constants/semipublic";

interface LogoIconProps {
  width?: number;
  height?: number;
  dark?: boolean;
  fill?: string;
  className?: string;
}

// Semipublic "SP" mark from the design system (assets/logo/sp-mark-color.svg):
// green brackets framing forest "sp" letterforms.
const MARK_BRACKET_POINTS = [
  "64.7 282.8 64.7 64.7 153.5 52.6 153.5 0 64.7 0 0 0 0 54.1 0 282.8 0 367.5 0 390.3 0 444.4 64.7 444.4 153.5 444.4 153.5 391.8 64.7 379.6 64.7 367.5 64.7 282.8",
  "568.5 0 479.7 0 479.7 52.6 568.5 64.7 568.5 282.8 568.5 379.6 479.7 391.8 479.7 444.4 568.5 444.4 633.2 444.4 633.2 406.4 633.2 390.3 633.2 54.1 633.2 0 568.5 0",
];

const MARK_LETTER_PATHS = [
  "M246.4,265.4c0-5.4-1.9-10.1-5.8-14.1-3.9-4-8.7-7.6-14.4-10.9-5.7-3.3-12-6.7-18.8-10-6.8-3.3-13-7.2-18.8-11.7-5.7-4.5-10.5-9.7-14.4-15.8-3.9-6-5.8-13.4-5.8-22.2s1.5-11.9,4.4-17c2.9-5.1,6.9-9.4,11.9-13,5-3.5,11-6.3,18-8.3,7-2,14.5-3,22.7-3s21.7.9,31.4,2.7c9.7,1.8,18.2,4.2,25.5,7.3-.8,6.7-2.5,13.8-5,21.4-2.5,7.6-4.9,14.5-7.2,20.8h-10.6l-13.1-31.9c-2.5-1-5.1-1.7-7.8-2-2.7-.3-5.7-.5-9.1-.5-6.3,0-11,1.8-14.2,5.5-3.2,3.6-4.8,7.8-4.8,12.3s1.9,9.7,5.8,13.6c3.9,3.9,8.6,7.5,14.4,10.8,5.7,3.3,12,6.7,18.8,10,6.8,3.3,13,7.1,18.8,11.4,5.7,4.3,10.5,9.3,14.4,15.2,3.9,5.8,5.8,12.8,5.8,20.9,0,14.4-5.8,25.7-17.5,33.9-11.7,8.2-27.3,12.3-46.9,12.3s-20.3-1-30.9-3.1c-10.6-2.1-19.8-5-27.5-8.8.4-7.5,2.2-15.1,5.3-22.8,3.1-7.7,6.5-14.6,10-20.6h10.6l15.3,34.1c2.3,1.7,4.8,2.7,7.7,3s5.5.5,8,.5c6,0,11.6-1.8,16.6-5.3,5-3.5,7.5-8.4,7.5-14.7Z",
  "M390.5,362h-86.3v-11.9c7.1-3.3,14-5.9,20.6-7.5v-177.5l-20.6-2.5v-11.9c3.3-1.5,7.4-2.8,12.3-4.1,4.9-1.3,9.9-2.3,15.2-3.3,5.2-.9,10.3-1.7,15.2-2.3,4.9-.6,9-1.1,12.3-1.6h10.6v35h.3c3.5-10,9.1-18.3,16.7-25,7.6-6.7,18.2-10,31.7-10s17.5,1.5,24.9,4.5c7.4,3,13.6,7.7,18.8,14.1,5.1,6.4,9.1,14.5,11.9,24.4,2.8,9.9,4.2,21.7,4.2,35.5s-1.8,25-5.5,35.6c-3.6,10.6-8.9,19.6-15.6,26.9-6.8,7.3-15,12.9-24.7,16.9-9.7,4-20.7,5.9-33,5.9s-12.7-.5-16.7-1.4-8.4-2.5-13-4.8v45.6c3.3.4,6.7,1.2,10,2.4,3.3,1.2,6.9,2.9,10.6,5.2v11.9ZM400.5,164.4c-7.1,0-13.4,2.5-18.9,7.5-5.5,5-9.4,12.7-11.7,23.1v83.2c3.3,2.5,6.9,4.3,10.6,5.5,3.8,1.1,8.6,1.7,14.7,1.7,11.9,0,20.6-5.7,26.3-17.2,5.6-11.5,8.4-28.2,8.4-50.3s-2.1-30.3-6.4-39.5c-4.3-9.3-11.9-13.9-23-13.9Z",
];

export const DefaultLogoIcon = ({
  dark,
  height = 32,
  width,
  className,
}: LogoIconProps) => {
  const isDarkColorScheme = useMantineTheme().other.colorScheme === "dark";
  const letterColor =
    dark || isDarkColorScheme
      ? SEMIPUBLIC_COLORS.white
      : SEMIPUBLIC_COLORS.forest;

  return (
    <svg
      className={cx(
        "Icon",
        className,
        { [CS.textMetabaseBrand]: !dark },
        { [CS.textWhite]: dark },
      )}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 633.2 444.4"
      width={width}
      height={height}
      role="img"
      aria-label="Semipublic"
      data-testid="main-logo"
    >
      {MARK_BRACKET_POINTS.map((points) => (
        <polygon key={points} fill={SEMIPUBLIC_COLORS.green} points={points} />
      ))}
      {MARK_LETTER_PATHS.map((d) => (
        <path key={d} fill={letterColor} d={d} />
      ))}
    </svg>
  );
};

export function LogoIcon(props: LogoIconProps) {
  const [Component = DefaultLogoIcon] = PLUGIN_LOGO_ICON_COMPONENTS;
  return <Component {...props} />;
}
